/**
 * Pathfinder 2e stat blocks: renders RulesBlocks from the codex (name and action glyphs, type,
 * trait chips, labelled header lines, body, degrees of success) and shows them in a hover
 * tooltip for any element with a data-ref attribute.
 */
import type { Codex, RulesBlock } from '../shared/view.ts';

function esc(s: unknown): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

/** Action glyphs: ◆ ◆◆ ◆◆◆, ⟲ reaction, ◇ free action. */
export function glyph(cost: number | string | undefined): string {
  if (cost === undefined || cost === '') return '';
  const g = cost === 'reaction' || cost === -1 ? '⟲' : cost === 'free' || cost === 0 || cost === '0' ? '◇' : '◆'.repeat(Number(cost));
  return `<span class="glyph" title="${esc(glyphName(cost))}">${g}</span>`;
}

function glyphName(cost: number | string): string {
  if (cost === 'reaction' || cost === -1) return 'Reaction';
  if (cost === 'free' || cost === 0 || cost === '0') return 'Free Action';
  return Number(cost) === 1 ? 'Single Action' : `${cost} Actions`;
}

/** Escapes text and styles any action glyphs inside it ("Activate ◆ (manipulate)"). */
export function rich(text: string): string {
  return esc(text).replace(/[◆◇⟲]+/g, (g) => `<span class="glyph">${g}</span>`);
}

export function traitChips(traits: string[], rarity?: string): string {
  const chips = [
    rarity ? `<span class="trait ${esc(rarity)}">${esc(rarity)}</span>` : '',
    ...traits.map((t) => `<span class="trait">${esc(t)}</span>`),
  ].join('');
  return chips ? `<div class="rb-traits">${chips}</div>` : '';
}

export function blockHtml(b: RulesBlock, opts: { compact?: boolean; note?: string } = {}): string {
  const fields = b.fields.map((line) => `<p>${line.map((e) => `<b>${esc(e.label)}</b> ${rich(e.text)}`).join('; ')}</p>`).join('');
  const degrees = (b.degrees ?? []).map((d) => `<p class="rb-deg"><b>${esc(d.label)}</b> ${rich(d.text)}</p>`).join('');
  const extra = (b.extra ?? []).map((e) => `<p><b>${esc(e.label)}</b> ${rich(e.text)}</p>`).join('');
  const sep = '<hr>';
  return `<div class="rb ${opts.compact ? 'compact' : ''}">
    <div class="rb-head"><span class="rb-name">${esc(b.name)} ${glyph(b.cost)}</span>${b.type ? `<span class="rb-type">${esc(b.type)}</span>` : ''}</div>
    ${traitChips(b.traits, b.rarity)}
    ${fields ? `<div class="rb-fields">${fields}</div>` : ''}
    ${(fields || b.traits.length) && (b.body || degrees) ? sep : ''}
    ${b.body ? `<p class="rb-body">${rich(b.body)}</p>` : ''}
    ${degrees}
    ${extra ? `${sep}${extra}` : ''}
    ${opts.note ? `<p class="rb-note">${esc(opts.note)}</p>` : ''}
  </div>`;
}

/** `data-ref` (and an optional note, e.g. why an action is unavailable) for hoverable elements. */
export function refAttr(ref: string | undefined, note?: string): string {
  if (!ref) return '';
  return ` data-ref="${esc(ref)}"${note ? ` data-ref-note="${esc(note)}"` : ''}`;
}

// ------------------------------------------------------------ hover tooltip

/** Rules blocks on hover for anything with `data-ref`, in this document or another (the log pop-out). */
export function installRulesTooltip(codex: () => Codex | null, doc: Document = document): void {
  const win = doc.defaultView ?? window;
  const tip = doc.createElement('div');
  tip.id = 'rules-tip';
  tip.className = 'hidden';
  doc.body.appendChild(tip);
  let current: HTMLElement | null = null;
  let timer = 0;
  let shown = '';
  // Where the pointer last was: a panel re-rendered under it is searched there for the new anchor.
  let lastX = -1, lastY = -1;

  /** Beside the anchor if there's room, otherwise above or below it. */
  const place = (anchor: HTMLElement) => {
    const r = anchor.getBoundingClientRect();
    const w = tip.offsetWidth, h = tip.offsetHeight, vw = win.innerWidth, vh = win.innerHeight, m = 8;
    let x: number, y: number;
    if (r.right + m + w <= vw - m) [x, y] = [r.right + m, r.top];
    else if (r.left - m - w >= m) [x, y] = [r.left - m - w, r.top];
    else [x, y] = [Math.min(vw - w - m, r.left), r.bottom + m + h <= vh - m ? r.bottom + m : r.top - m - h];
    tip.style.left = `${Math.max(m, x)}px`;
    tip.style.top = `${Math.max(m, Math.min(vh - h - m, y))}px`;
  };

  /** Shows the block beside its anchor, rewriting the tip only when its text changed. */
  const show = (anchor: HTMLElement, block: RulesBlock) => {
    const html = blockHtml(block, { note: anchor.dataset.refNote });
    if (html !== shown) tip.innerHTML = shown = html;
    tip.classList.remove('wide', 'wider', 'hidden');
    // Too tall for the screen: lay the block out wide, in two columns and then three, rather than cut it off.
    for (const layout of ['wide', 'wider']) {
      if (tip.scrollHeight <= tip.clientHeight) break;
      tip.classList.add(layout);
    }
    place(anchor);
  };

  const hide = () => {
    win.clearTimeout(timer);
    current = null;
    tip.classList.add('hidden');
  };

  /**
   * The rules anchor around `node`: the nearest data-ref element, unless a tag with its own title
   * note (an item's blessed, HP or broken tag) comes first, which shows only that note.
   */
  const anchorAt = (node: Element | null | undefined): HTMLElement | null => {
    const el = node?.closest<HTMLElement>('[data-ref], .tag[title]');
    return el?.dataset.ref ? el : null;
  };

  /** The anchor, or the element with its rules ref now under the pointer if a re-render replaced it. */
  const reanchor = (el: HTMLElement): HTMLElement | null => {
    if (el.isConnected) return el;
    const under = anchorAt(doc.elementFromPoint(lastX, lastY));
    return under && under.dataset.ref === el.dataset.ref ? under : null;
  };

  doc.addEventListener('mousemove', (e) => {
    lastX = e.clientX;
    lastY = e.clientY;
  }, { passive: true });
  doc.addEventListener('mouseover', (e) => {
    lastX = e.clientX;
    lastY = e.clientY;
    const el = anchorAt(e.target as Element);
    if (el === current) return;
    if (!el) return hide();
    const block = codex()?.[el.dataset.ref!];
    if (!block) return hide();
    // The same rules ref (often the same element, re-rendered): keep the tooltip or its timer going.
    if (current?.dataset.ref === el.dataset.ref) {
      current = el;
      if (!tip.classList.contains('hidden')) show(el, block);
      return;
    }
    current = el;
    win.clearTimeout(timer);
    timer = win.setTimeout(() => {
      const anchor = current && reanchor(current);
      if (!anchor) return hide();
      current = anchor;
      show(anchor, block);
    }, 220);
  });
  doc.addEventListener('mousedown', hide);
  win.addEventListener('blur', hide);
}
